import { CONFIG } from 'config/environment_variables';
import { emitErrorLog, emitInfoLog, emitWarnLog } from './observability/logger';
import { AnyValueMap } from '@opentelemetry/api-logs';

export type LogLevel = 'log' | 'warn' | 'error' | 'info' | 'debug';

interface LogParams {
  message: string;
  data?: unknown;
  enableInProduction?: boolean;
  [key: string]: unknown;
}

type LoggerMethod = (params: LogParams) => void;

type LoggerType = {
  [K in LogLevel]: LoggerMethod;
};

const createLoggerMethod =
  (level: LogLevel): LoggerMethod =>
  (params: LogParams) => {
    const { message, enableInProduction = false, ...rest } = params;

    if (level === 'error') {
      emitErrorLog({
        body: {
          message,
          data: rest as unknown as AnyValueMap,
        },
        attrs: {
          source: 'Logger',
          level,
        },
      });
    }

    if (CONFIG.NODE_ENV === 'development' || enableInProduction) {
      const hasAdditionalParams = Object.keys(rest).length > 0;

      if (level === 'warn') {
        emitWarnLog({
          body: {
            message,
            data: rest as unknown as AnyValueMap,
          },
          attrs: {
            source: 'Logger',
            level,
          },
        });
      }

      if (level === 'info' || level === 'log') {
        emitInfoLog({
          body: {
            message,
            data: rest as unknown as AnyValueMap,
          },
          attrs: {
            source: 'Logger',
            level,
          },
        });
      }

      if (hasAdditionalParams) {
        // eslint-disable-next-line no-console
        (console[level] as (...data: unknown[]) => void)(message, rest);
      } else {
        // eslint-disable-next-line no-console
        (console[level] as (...data: unknown[]) => void)(message);
      }
    }
  };

export const Logger: LoggerType = {
  log: createLoggerMethod('log'),
  warn: createLoggerMethod('warn'),
  error: createLoggerMethod('error'),
  info: createLoggerMethod('info'),
  debug: createLoggerMethod('debug'),
};
